import axios from 'axios';
import { getSession, logout } from './auth';

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: unknown;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = payload.code;
    this.details = payload.details;
  }
}

export const apiClient = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:5000/api/v1',
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = getSession()?.token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError<{ error?: ApiErrorPayload }>(error)) {
      const payload = error.response?.data.error;
      const isLoginRequest = String(error.config?.url ?? '').includes('/auth/login');
      if (error.response?.status === 401 && !isLoginRequest) {
        logout();
        if (window.location.pathname !== '/login') window.location.assign('/login');
      }
      throw new ApiError(error.response?.status ?? 0, {
        code: payload?.code ?? 'NETWORK_ERROR',
        message: payload?.message ?? 'Unable to reach the server. Please try again.',
        ...(payload?.details === undefined ? {} : { details: payload.details }),
      });
    }
    throw error;
  },
);
